import { useEffect } from "react";
import {
  DEFAULT_OG_IMAGE,
  PRIMARY_KEYWORDS,
  SITE_NAME,
  buildAbsoluteUrl,
} from "../../seo/siteSeo.js";

const MANAGED_ATTRIBUTE = "data-fapify-seo";
const DEFAULT_ROBOTS =
  "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === null || value === undefined || value === false) {
      element.removeAttribute(key);
      return;
    }

    element.setAttribute(key, value);
  });

  element.setAttribute(MANAGED_ATTRIBUTE, "true");

  return element;
};

const removeManagedNode = (selector) => {
  const element = document.head.querySelector(selector);
  if (element && element.getAttribute(MANAGED_ATTRIBUTE) === "true") {
    element.remove();
  }
};

const ensureLink = (rel, href) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
  element.setAttribute(MANAGED_ATTRIBUTE, "true");
};

const syncJsonLd = (jsonLd) => {
  const activeIds = jsonLd.map((_, index) => `fapify-seo-jsonld-${index}`);

  jsonLd.forEach((schema, index) => {
    const scriptId = activeIds[index];
    let element = document.head.querySelector(`#${scriptId}`);

    if (!element) {
      element = document.createElement("script");
      element.type = "application/ld+json";
      element.id = scriptId;
      document.head.appendChild(element);
    }

    element.textContent = JSON.stringify(schema);
    element.setAttribute(MANAGED_ATTRIBUTE, "true");
  });

  Array.from(
    document.head.querySelectorAll(`script[${MANAGED_ATTRIBUTE}="true"]`),
  )
    .filter((element) => element.type === "application/ld+json")
    .filter((element) => !activeIds.includes(element.id))
    .forEach((element) => element.remove());
};

const SeoHead = ({
  title,
  description,
  path = "/",
  canonicalPath,
  keywords = PRIMARY_KEYWORDS,
  ogType = "website",
  image = DEFAULT_OG_IMAGE,
  robots,
  noindex = false,
  jsonLd = [],
  language = "en",
}) => {
  useEffect(() => {
    const absoluteUrl = buildAbsoluteUrl(path);
    const canonicalUrl = buildAbsoluteUrl(canonicalPath ?? path);

    document.documentElement.setAttribute("lang", language);
    document.title = title;

    ensureMeta(`meta[name="description"]`, {
      name: "description",
      content: description,
    });
    ensureMeta(`meta[name="keywords"]`, {
      name: "keywords",
      content: keywords.join(", "),
    });
    ensureMeta(`meta[name="author"]`, {
      name: "author",
      content: SITE_NAME,
    });
    ensureMeta(`meta[name="robots"]`, {
      name: "robots",
      content: noindex ? "noindex,follow" : robots || DEFAULT_ROBOTS,
    });

    ensureMeta(`meta[property="og:type"]`, {
      property: "og:type",
      content: ogType,
    });
    ensureMeta(`meta[property="og:url"]`, {
      property: "og:url",
      content: absoluteUrl,
    });
    ensureMeta(`meta[property="og:title"]`, {
      property: "og:title",
      content: title,
    });
    ensureMeta(`meta[property="og:description"]`, {
      property: "og:description",
      content: description,
    });
    ensureMeta(`meta[property="og:image"]`, {
      property: "og:image",
      content: image,
    });
    ensureMeta(`meta[property="og:site_name"]`, {
      property: "og:site_name",
      content: SITE_NAME,
    });

    ensureMeta(`meta[property="twitter:card"]`, {
      property: "twitter:card",
      content: "summary_large_image",
    });
    ensureMeta(`meta[property="twitter:url"]`, {
      property: "twitter:url",
      content: canonicalUrl,
    });
    ensureMeta(`meta[property="twitter:title"]`, {
      property: "twitter:title",
      content: title,
    });
    ensureMeta(`meta[property="twitter:description"]`, {
      property: "twitter:description",
      content: description,
    });
    ensureMeta(`meta[property="twitter:image"]`, {
      property: "twitter:image",
      content: image,
    });

    ensureLink("canonical", canonicalUrl);
    syncJsonLd(jsonLd);

    return () => {
      removeManagedNode(`link[rel="canonical"]`);
    };
  }, [
    canonicalPath,
    description,
    image,
    jsonLd,
    keywords,
    language,
    noindex,
    ogType,
    path,
    robots,
    title,
  ]);

  return null;
};

export default SeoHead;
